import { useEffect } from "react";

const ALERT_TITLE = "Balasan dari Kakak Siaga";
const DOT_COLOR = "#E53935";

const getFaviconLink = () =>
  document.querySelector<HTMLLinkElement>('link[rel~="icon"]');

// Draws the current favicon with a red dot; resolves null when the image
// cannot be read (e.g. blocked by CORS), so the title alone is used.
const createDotFavicon = (href: string): Promise<string | null> =>
  new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        if (!context) return resolve(null);
        context.drawImage(image, 0, 0, size, size);
        context.beginPath();
        context.arc(size - 8, 8, 7, 0, 2 * Math.PI);
        context.fillStyle = DOT_COLOR;
        context.fill();
        context.lineWidth = 2;
        context.strokeStyle = "#ffffff";
        context.stroke();
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = href;
  });

// While the tab is in the background and the chat has unread replies, shows
// "(N) Balasan dari Kakak Siaga" as the tab title and a red dot on the favicon.
// Both are restored when the tab is visible again or the replies are read.
export const useOdooLiveChatTabAlert = (unreadCount: number) => {
  useEffect(() => {
    if (unreadCount <= 0) return;

    let isActive = false;
    let isCancelled = false;
    let originalTitle = "";
    let alertTitle = "";
    let originalFaviconHref: string | null = null;

    const restore = () => {
      if (!isActive) return;
      isActive = false;
      // Do not overwrite a title the page changed in the meantime.
      if (document.title === alertTitle) document.title = originalTitle;
      const link = getFaviconLink();
      if (link && originalFaviconHref !== null) {
        link.href = originalFaviconHref;
      }
    };

    const apply = () => {
      if (isActive || document.visibilityState !== "hidden") return;
      isActive = true;
      originalTitle = document.title;
      alertTitle = `(${unreadCount}) ${ALERT_TITLE}`;
      document.title = alertTitle;

      const link = getFaviconLink();
      if (!link) return;
      originalFaviconHref = link.href;
      createDotFavicon(link.href).then((dotHref) => {
        if (dotHref && isActive && !isCancelled) link.href = dotHref;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") apply();
      else restore();
    };

    apply();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      isCancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      restore();
    };
  }, [unreadCount]);
};
